import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { of, Subscription, catchError, finalize, map, tap, filter } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';

import { ADD_KEYWORDS, HELP_KEYWORDS, OPT_IN_KEYWORDS, OPT_OUT_KEYWORDS } from '@shared/sms/constants';
import { Location } from '@shared/location/models';
import { Account } from '@shared/account/models';
import {
  DynamicField,
  ValidationService,
  forbiddenWordsValidator,
  IDefaultAutomationsValues,
} from '@tablesready/common-ui';
import { ApiClientService } from '@tablesready/api-client';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { FrontendDropdownPlainComponent } from '@tablesready/frontend-dropdown';
import { PlanRestrictionsAlertService, PricePlansRestrictionsService, WhoService } from '../../../services';
import { PlansAlertData } from '../../../models';

@Component({
  selector: 'tablesready-automations',
  templateUrl: './automations.component.html',
  styleUrls: ['./automations.component.scss'],
})
export class AutomationsComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('cancelDropdown') cancelDropdown?: FrontendDropdownPlainComponent;
  @ViewChild('checkinDropdown') checkinDropdown?: FrontendDropdownPlainComponent;
  @ViewChild('confirmDropdown') confirmDropdown?: FrontendDropdownPlainComponent;
  @ViewChild('lateDropdown') lateDropdown?: FrontendDropdownPlainComponent;
  location: Location;
  account: Account;
  automationsForm: UntypedFormGroup;
  maxNonAnsiChars: number;
  maxAnsiChars: number;
  submitting = false;
  smsPhoneNumber = '';
  defaultValues: IDefaultAutomationsValues;
  isLocationOnBusinessPlan = true;
  dynamicFieldOptions: DynamicField[];
  plansAlertData: PlansAlertData;

  private subscriptions = new Subscription();
  private readonly forbiddenStaticKeywords = [
    ...ADD_KEYWORDS,
    ...OPT_IN_KEYWORDS,
    ...OPT_OUT_KEYWORDS,
    ...HELP_KEYWORDS,
  ];

  constructor(
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly fb: UntypedFormBuilder,
    private readonly validationService: ValidationService,
    private readonly http: ApiClientService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly planRestrictionsAlertService: PlanRestrictionsAlertService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
    this.maxAnsiChars = Number(this.appVariables.WHITELABEL_SMS_CHARS);
    this.maxNonAnsiChars = Number(this.appVariables.WHITELABEL_SMS_NON_ANSI_CHARS);
  }

  ngOnInit(): void {
    const initialSubscription = this.authRestaurantService.select()
      .pipe(
        filter(user => user.location !== undefined),
        tap(user => {
          const { account, location } = user;
          this.location = location;
          this.account = account;
          this.defaultValues = this.getDefaults(this.location);
          this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
          this.plansAlertData = this.planRestrictionsAlertService
            .getAlertData(
              account.subscriptionType,
              account.recurly,
              location.recurly,
            );
          this.dynamicFieldOptions = [
            {
              name: '{date}',
              toolTip: `The scheduled date and time for a ${user.location.displayOptions.labels.reservation}.`,
              defaultLength: 25,
            },
            {
              name: '{link}',
              toolTip: 'Link for customers to see their place in line if the public waitlist/reservation view mode is enabled.',
              defaultLength: 'https://host.tablesready.com/p/waitlist/'.length + 8,
            },
            {
              name: '{business}',
              toolTip: 'The name of your business.',
              defaultLength: 20,
            },
            {
              name: '{appointment}',
              toolTip: `The name of ${user.location.displayOptions.labels.reservation} label.`,
              defaultLength: 20,
            },
          ];
          const {automations, selfSignUp} = this.defaultValues;

          const publicVenueNameValidators = [
            Validators.pattern(/^[-a-zA-Z0-9_]*$/),
            Validators.minLength(3),
          ];

          if (selfSignUp.enabled) {
            publicVenueNameValidators.unshift(Validators.required);
          }

          this.automationsForm = this.fb.group({
            automations: this.fb.group({
              cancel: this.fb.group({
                enabled: [automations.cancel.enabled, [Validators.required]],
                keywords: [automations.cancel.keywords, [
                  Validators.required,
                  forbiddenWordsValidator([
                    ...this.forbiddenStaticKeywords,
                    ...automations.checkin.keywords,
                    ...automations.confirm.keywords,
                    ...automations.late.keywords,
                  ]),
                ]],
                reply: [automations.cancel.reply, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars),
                ]],
                stripeReplyOnPublicPageEnabled: [automations.cancel.stripeReplyOnPublicPageEnabled, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars),
                ]],
                stripeReplyOnPublicPageDisabled: [automations.cancel.stripeReplyOnPublicPageDisabled, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars),
                ]],
                stripeReplyOnPaymentRefunded: [automations.cancel.stripeReplyOnPaymentRefunded, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars),
                ]],
              }),
              checkin: this.fb.group({
                enabled: [automations.checkin.enabled, [Validators.required]],
                keywords: [automations.checkin.keywords, [
                  Validators.required,
                  forbiddenWordsValidator([
                    ...this.forbiddenStaticKeywords,
                    ...automations.cancel.keywords,
                    ...automations.confirm.keywords,
                    ...automations.late.keywords,
                  ]),
                ]],
                reply: [automations.checkin.reply, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars)
                ]],
              }),
              confirm: this.fb.group({
                enabled: [automations.confirm.enabled, [Validators.required]],
                keywords: [automations.confirm.keywords, [
                  Validators.required,
                  forbiddenWordsValidator([
                    ...this.forbiddenStaticKeywords,
                    ...automations.cancel.keywords,
                    ...automations.checkin.keywords,
                    ...automations.late.keywords,
                  ]),
                ]],
                reply: [automations.confirm.reply, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars)
                ]],
              }),
              late: this.fb.group({
                enabled: [automations.late.enabled, [Validators.required]],
                keywords: [automations.late.keywords, [
                  Validators.required,
                  forbiddenWordsValidator([
                    ...this.forbiddenStaticKeywords,
                    ...automations.cancel.keywords,
                    ...automations.checkin.keywords,
                    ...automations.confirm.keywords,
                  ]),
                ]],
                reply: [automations.late.reply, [
                  Validators.required,
                  this.validationService.textLengthValidator(this.maxAnsiChars, this.maxNonAnsiChars),
                ]],
                offset: [automations.late.offset, [Validators.required, Validators.min(1), Validators.max(120)]],
              }),
            }),
            selfSignUp: this.fb.group({
              enabled: [selfSignUp.enabled, Validators.required],
              publicVenueName: [selfSignUp.publicVenueName, publicVenueNameValidators, [
                (control: AbstractControl) => this.validationService.publicVenueNameUsedValidator(control.value, this.location._id)
              ]],
              reply: [selfSignUp.reply, [
                Validators.required,
                this.validationService.textWatcherValidator(this.location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
                this.validationService.textWatcherValidator(this.location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
            }),
          });
          this.initSubscriptions();
          this.toggleFormEnabled();
          this.cdr.detectChanges();
        }),
      ).subscribe();
    this.subscriptions.add(initialSubscription);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled?.previousValue !== undefined) {
      this.toggleFormEnabled();
      this.cdr.detectChanges();
    }
  }

  getDefaults(user: Location): IDefaultAutomationsValues {
    return {
      automations: cloneDeep(user.automations) || {
        cancel: {
          enabled: false,
          keywords: ['cancel', 'remove'],
          reply: 'You have been removed from the waitlist. We hope to see you again in the future!',
          stripeReplyOnPublicPageEnabled: 'Since you have already made a payment and you are inside the no-cancellation window, please manage your {appointment} here: {link}.',
          stripeReplyOnPublicPageDisabled: 'Since you have already made a payment and you are inside the no-cancellation window, please contact us via phone or email to make changes to your {appointment}.',
          stripeReplyOnPaymentRefunded: 'Your {appointment} have been canceled and fully refunded. It may take a few days for the money to reach your bank account.',
        },
        checkin: {
          enabled: true,
          keywords: ['here', 'arrived', 'check-in', 'checkin'],
          reply: `Thanks for checking in. We'll text again when we're ready for you.`,
        },
        confirm: {
          enabled: false,
          keywords: ['Y', 'confirm'],
          reply: 'Thank you for confirming your {appointment}. We look forward to seeing you on {date}.',
        },
        late: {
          enabled: false,
          keywords: ['late', 'delayed'],
          reply: 'Thanks for letting us know! Please check in when you arrive and we\'ll do our best to accommodate you.',
          offset: 15,
        },
      },
      selfSignUp: cloneDeep(user.selfSignUp) || {
        enabled: false,
        reply: `You have successfully added yourself to the ${user.name} waitlist. We will text you when it is your turn.`,
        publicVenueName: user.name.replace(/\W+/g, '').toLowerCase(),
      },
    };
  }

  submit() {
    this.automationsForm.markAsPristine();
    this.whoService.updateAutomations(this.automationsForm.value)
      .pipe(
        catchError(error => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: error
          });

          return of(error);
        }),
        tap(response => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Updated:',
            body: response
          });
        }),
        finalize(() => this.submitting = false),
      )
      .subscribe();

    this.cancelDropdown?.resetItems();
    this.checkinDropdown?.resetItems();
    this.confirmDropdown?.resetItems();
    this.lateDropdown?.resetItems();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getSmsPhoneNumber() {
    return this.http.get<any>('/v.1/twilio/smsPhoneNumber').pipe(
      map(({phoneNumber}) => {
        if(phoneNumber) {
          return phoneNumber;
        } else {
          throw new Error();
        }
      })
    )
  }

  get publicVenueNameControl(): AbstractControl {
    return this.automationsForm.get('selfSignUp.publicVenueName');
  }

  private initSubscriptions() {
    const keywordProperties = [
      'automations.cancel.keywords',
      'automations.checkin.keywords',
      'automations.confirm.keywords',
      'automations.late.keywords',
    ];
    for (let i = 0; i < keywordProperties.length; i++) {
      const subscription = this.automationsForm.get(keywordProperties[i]).valueChanges
        .pipe(tap(() => {
          // Iterate over each input to update validation
          for (let j = 0; j < keywordProperties.length; j++) {
            // Skip changed input, because it doesn't make sense to update its validation
            if (i === j) {
              continue;
            }
            const forbiddenKeywords = [...this.forbiddenStaticKeywords];
            for (let k = 0; k < keywordProperties.length; k++) {
              // Skip adding own values as forbidden keywords
              if (j === k) {
                continue;
              }
              if (this.automationsForm.get(keywordProperties[k]).valid) {
                forbiddenKeywords.push(...this.automationsForm.get(keywordProperties[k]).value);
              }
            }
            this.automationsForm.get(keywordProperties[j]).setValidators([
              Validators.required,
              forbiddenWordsValidator(forbiddenKeywords),
            ]);
            if (i !== j) {
              this.automationsForm.get(keywordProperties[j]).updateValueAndValidity({onlySelf: false, emitEvent: false});
            }
          }
        }))
        .subscribe();
      this.subscriptions.add(subscription);
    }

    const selfSignupEnabledSubscription = this.automationsForm.get('selfSignUp.enabled').valueChanges
      .pipe(tap((value: boolean) => {
        const validators = [
          Validators.pattern(/^[-a-zA-Z0-9_]*$/),
          Validators.minLength(3),
        ];
        if (value) {
          validators.unshift(Validators.required);
        }
        this.publicVenueNameControl.setValidators(validators);
        this.publicVenueNameControl.markAsTouched();
        this.publicVenueNameControl.updateValueAndValidity();
      }))
      .subscribe();
    this.subscriptions.add(selfSignupEnabledSubscription);

    this.subscriptions.add(
      this.getSmsPhoneNumber().subscribe(
        (phoneNumber) => {
          this.smsPhoneNumber = phoneNumber;
          this.cdr.detectChanges();
        }
      )
    );

    this.subscriptions.add(
      this.automationsForm.statusChanges.subscribe(() => this.cdr.detectChanges())
    );
  }

  private toggleFormEnabled() {
    if (!this.automationsForm) {
      return;
    }

    if (!this.isLocationOnBusinessPlan) {
      this.automationsForm.disable({onlySelf: true, emitEvent: false});
    } else {
      this.automationsForm.enable({onlySelf: true, emitEvent: false});
    }
  }
}
